import Big from 'big.js'
import _ from 'lodash'

import { assertNever } from 'types/assertNever'

type FormattedPart =
  | { type: 'subscript'; value: string }
  | { type: 'part'; value: string; color?: string }

type FormattedNumber = FormattedPart[]

const part = (value: string, color?: string): FormattedPart => ({
  type: 'part',
  value,
  color,
})
const subscript = (value: string): FormattedPart => ({
  type: 'subscript',
  value,
})

export const prettyPrintFormattedNumber = (formatted: FormattedNumber) =>
  formatted
    .map(x => (x.type === 'subscript' ? `{${x.value}}` : x.value))
    .join('')

export const compactFormatterToFormattedNumberPretty = (
  v: Big | number,
  currencySymbol?: CurrencySymbol,
): string =>
  prettyPrintFormattedNumber(
    compactFormatterToFormattedNumber(v, currencySymbol),
  )

type CurrencySymbol = 'USD'
const printCurrencySymbol = (symbol: CurrencySymbol) => {
  switch (symbol) {
    case 'USD':
      return '$'
    default:
      return assertNever(symbol)
  }
}

export const compactFormatterToFormattedNumber = (
  v: Big | number,
  currencySymbol?: CurrencySymbol,
): FormattedNumber => {
  if (_.isNil(v)) return []
  const b = _.isNumber(v) ? Big(v) : v
  const withSignAndUSD = (v: FormattedNumber): FormattedNumber => [
    ...(b.lt(0) ? [part('-')] : []),
    ...(currencySymbol ? [part(printCurrencySymbol(currencySymbol))] : []),
    ...v,
  ]

  const zeroValue = withSignAndUSD([{ type: 'part', value: '0' }])

  if (b.eq(0)) return zeroValue

  const abs = b.abs()

  if (abs.lt(0.0001)) {
    const { zeroCount, digits } = formatTinyNumber(abs)
    if (digits.length === 0) return zeroValue
    return withSignAndUSD([
      part('0.0'),
      subscript(zeroCount.toString()),
      part(digits),
    ])
  }

  if (abs.gte(10000)) {
    const formatted = abs
      .toNumber()
      .toLocaleString('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        minimumSignificantDigits: 4,
        maximumSignificantDigits: 4,
      })
      .replace('K', 'k')
    const lastChar = formatted.slice(-1)
    const colorScheme: Record<string, string> = {
      T: '#4e342e',
      B: '#6a1b9a',
      M: '#0277bd',
      k: '#ff8f00',
    }
    return withSignAndUSD([
      part(formatted.slice(0, -1)),
      part(lastChar, colorScheme[lastChar]),
    ])
  }

  const decimals = abs.gte(1000) ? 1 : abs.gte(100) ? 2 : abs.gte(10) ? 3 : 4
  return withSignAndUSD([
    part(abs.round(decimals, 1).toFixed(decimals).slice(0, 6)),
  ])
}

// Helper for formatting very small numbers
const formatTinyNumber = (abs: Big): { zeroCount: number; digits: string } => {
  const decimal = abs.toFixed()
  const fracPart = decimal.split('.')[1] || ''
  const match = fracPart.match(/^0+/)
  const zeroCount = match ? match[0].length : 0
  const nonZeroDigitsLength = zeroCount < 10 ? 2 : 1
  const digits = fracPart
    .slice(zeroCount)
    .padEnd(nonZeroDigitsLength + 1, '0')
    .slice(0, nonZeroDigitsLength + 1)
  return {
    zeroCount,
    digits: Big(digits)
      .div(10)
      .round()
      .toString()
      .slice(0, nonZeroDigitsLength),
  }
}

export type CompactDisplayModeFormatter = (
  b: Big,
  currencySymbol?: CurrencySymbol,
) => React.ReactElement

export const compactFormatter = (
  v: Big | number,
  currencySymbol?: CurrencySymbol,
): React.ReactElement => {
  const res = compactFormatterToFormattedNumber(v, currencySymbol)
  return (
    <>
      {res.map((r, idx) => {
        switch (r.type) {
          case 'part':
            return (
              <span
                style={{
                  color: r.color,
                }}
                key={idx}
              >
                {r.value}
              </span>
            )
          case 'subscript':
            return (
              <sub
                key={idx}
                style={{
                  fontSize: '0.8em',
                  color: '#FFB300',
                  fontWeight: 'bold',
                  padding: `0 0.1rem`,
                }}
              >
                {r.value}
              </sub>
            )
          default:
            return assertNever(r)
        }
      })}
    </>
  )
}
